import { title } from 'framer-motion/client';
import { List } from 'lucide-react';
import { generateSectionId } from '../../../HelpCenterComponents/ArticleLayout';

export const UsingSpatialGridContentBody = ({ activeLeft }) => {
  const contentData = {
    0: {
      intro:
        'The Spatial Grid Dashboard is the central workspace for managing experiences, projects, and deployments. It provides a consolidated view of active projects, recent activity, assigned tasks, and system status. Enterprise users can quickly access ongoing initiatives, track progress, and understand what is deployed, in development, or pending review. The dashboard is designed to give business, technical, and operational teams a shared, real-time view of platform activity.',
      sections: [
        {
          type: 'text',
          heading: 'Overview',
          paragraphs: [
            'The Spatial Grid Dashboard acts as the central control center for all platform activity. It brings together projects, experiences, deployments, analytics summaries, and recent actions into a single, unified view.',
            'Designed for enterprise environments, the dashboard allows business leaders, technical teams, and operational users to quickly understand the current state of work across the organization. Whether tracking live deployments, reviewing work in progress, or monitoring system health, the dashboard provides immediate clarity without requiring users to navigate multiple screens.',
          ],
        },
        {
          type: 'text',
          heading: 'Key Sections of the Dashboard',
          subSections: [
            {
              title: 'Active Projects',
              paragraphs: [
                'The **Active Projects** section displays all projects currently in development, under review, or live in production. Each project card provides high-level status indicators such as stage, last updated time, assigned teams, and deployment state.',
                'This allows users to quickly identify priorities, monitor progress, and access projects that require action—such as reviews, updates, or approvals.',
              ],
            },
            {
              title: 'Start a New Project',
              paragraphs: [
                'The **Start a New Project** option enables users to initiate new immersive initiatives quickly. Projects can be created using:',
              ],
              list: [
                'Device-specific publishing options',
                'Pre-configured industry templates',
                'Existing project templates for reuse',
              ],
              afterParagraphs: [
                'This flexibility ensures faster setup while maintaining consistency with enterprise standards, device requirements, and industry workflows.',
              ],
            },
            {
              title: 'Recent Activity',
              paragraphs: [
                'The **Recent Activity** feed provides a chronological view of actions taken across the platform. This includes edits, deployments, reviews, asset updates, and configuration changes made by team members.',
                'By offering transparency into who did what and when, this section supports accountability, collaboration, and faster issue resolution across distributed teams.',
              ],
            },
            {
              title: 'Industry Templates',
              paragraphs: [
                'The **Industry Templates** section gives users access to pre-built templates tailored to specific industries and use cases such as manufacturing, pharma, mining, defence, real estate, tourism, and education.',
                'These templates provide ready-to-use structures, interactions, and workflows—helping teams accelerate development while aligning with domain-specific best practices.',
              ],
            },
            {
              title: 'Settings',
              paragraphs: [
                'The **Settings** area allows users to manage personal and organizational configurations. This includes:',
              ],
              list: [
                'User profile and account details',
                'License and subscription information',
                'Team and role management',
                'Billing and invoice access',
              ],
              afterParagraphs: [
                'Settings ensure enterprise users can manage access, usage, and compliance without relying on external support for routine administrative tasks.',
              ],
            },
            {
              title: 'Enterprise Value',
              paragraphs: [
                'The Spatial Grid Dashboard is designed to support informed decision-making across business, technical, and operational roles. By providing real-time visibility into what is live, what is in progress, and what requires attention, the dashboard reduces complexity and improves coordination.',
                'This centralized visibility enables enterprises to respond faster, manage initiatives more effectively, and maintain control as immersive programs scale across teams, locations, and devices.',
              ],
            },
          ],
        },
      ],
    },
    1: {
      intro:
        'Projects in Spatial Grid act as containers for immersive experiences, assets, logic, and analytics. Each project can represent a training program, simulation, marketing activation, or research initiative. Workspaces allow teams to organize projects by department, client, or use case. This structure supports enterprise governance by keeping content isolated, controlled, and aligned with organizational boundaries.',
      sections: [
        {
          heading: 'Project Overview',
          paragraphs: [
            'Projects in Spatial Grid act as structured containers for immersive initiatives. Each project brings together all components required to build, deploy, and manage an experience—ensuring everything related to a specific initiative is organized in one place.',
            'A project includes:',
          ],
          list: [
            'Immersive experiences and scenes',
            'Assets such as 3D models, animations, and environments',
            'Interaction logic and behavior configurations',
            'Analytics and usage dat',
            'Device and deployment settings',
          ],
          subSections: [
            {
              afterParagraphs: [
                'This structure helps teams manage complexity while maintaining clarity and traceability across the lifecycle of an initiative.',
              ],
            },
          ],
        },
        {
          heading: 'Common Project Types',
          paragraphs: [
            'Projects are typically mapped to real enterprise use cases, including:',
          ],
          subSections: [
            {
              title: 'Training Programs & SOP Modules',
              paragraphs: [
                'Projects represent structured training programs where SOPs are converted into immersive, scenario-based learning experiences. Each project can contain multiple modules, assessments, and deployment targets.',
              ],
            },
            {
              title: 'Simulation & Operational Readiness',
              paragraphs: [
                'Projects are used to simulate machinery, vehicles, facilities, or mission-critical operations—allowing teams to rehearse procedures and responses in a controlled environment.',
              ],
            },
            {
              title: 'Marketing & Engagement Campaigns',
              paragraphs: [
                'Projects support AR-based campaigns and interactive experiences deployed across print, digital, retail, and outdoor channels, with built-in analytics to measure engagement.',
              ],
            },
            {
              title: 'Research & Exploration Initiatives',
              paragraphs: [
                'Academic and research-focused projects enable experimentation, visualization, and exploration of complex systems, environments, or datasets.',
              ],
            },
          ],
        },
        {
          heading: 'Workspaces',
          paragraphs: [
            'Workspaces provide a higher-level organizational layer above projects. They allow enterprises to group related projects based on department, client, geography, or specific business units.',
            'By structuring content within workspaces, organizations can manage large portfolios of immersive initiatives without overlap or confusion.',
          ],
        },
        {
          heading: 'Organizing at Enterprise Scale',
          paragraphs: ['Workspaces are commonly used to:'],
          list: [
            'Separate internal teams or departments',
            'Manage client-specific or partner-specific initiatives',
            'Segment projects by region or location',
            'Isolate sensitive or regulated programs',
          ],
        },
        {
          paragraphs: [
            'This structure supports scalability while maintaining operational clarity',
          ],
        },
        {
          heading: 'Governance & Control',
          subSections: [
            {
              title: 'Ownership & Accountability',
              paragraphs: [
                'By isolating projects within defined workspaces, enterprises clearly establish ownership and responsibility. Teams know which projects they manage and what access they have.',
              ],
            },
            {
              title: 'Access Control & Security',
              paragraphs: [
                'Workspaces enforce access boundaries, ensuring users only see and interact with projects relevant to them. This is especially critical when working with external partners or vendors.',
              ],
            },
            {
              title: 'Review & Compliance',
              paragraphs: [
                'Controlled environments allow enterprises to implement approval workflows, audits, and compliance checks before experiences are deployed.',
              ],
            },
            {
              title: 'Reduced Risk at Scale',
              paragraphs: [
                'As immersive initiatives grow across teams and regions, workspace-based governance helps prevent conflicts, accidental changes, and unauthorized access—ensuring confidence and control at scale.',
              ],
            },
          ],
        },
      ],
    },
    2: {
      intro:
        'The Grid Editor is the primary environment for creating and configuring immersive experiences. It enables teams to assemble scenes, define interactions, configure behaviors, and manage experience flow. Designed for both technical and semi-technical users, the Grid Editor supports visual configuration alongside structured logic. This allows complex spatial experiences to be built, reviewed, and refined without constant low-level development changes.',
      sections: [
        {
          heading: 'Editor Overview',
          paragraphs: [
            'The Grid Editor is the core environment within Spatial Grid where immersive experiences are created, configured, and managed. It brings together visual design, interaction logic, and optional code customization into a single workspace.',
            'Teams use the Grid Editor to assemble scenes, place and configure assets, define interactions, and control the overall experience flow. It is designed to support collaboration between technical users and non-technical stakeholders, allowing experiences to be built, reviewed, and refined efficiently.',
          ],
        },
        {
          heading: 'Design, Preview & Code Functionality',
          paragraphs: [
            'The Grid Editor is organized into three primary modes—**Design, Preview**, and **Code**—each serving a specific purpose in the creation workflow.',
          ],
          subSections: [
            {
              title: 'Design',
              paragraphs: [
                'The Design mode is used to visually build and edit experiences. Users can place assets, configure scenes, define interactions, and adjust layouts using intuitive tools without writing code.',
              ],
            },
            {
              title: 'Preview',
              paragraphs: [
                'Preview mode allows users to experience the project as an end user would. Changes made in Design or Code can be instantly previewed to validate interactions, flows, and visual behavior before publishing.',
              ],
            },
            {
              title: 'Code',
              paragraphs: [
                'The Code tab allows advanced users to add as much or as little code as needed to enhance functionality. This enables fine-grained control, custom logic, or integration scenarios—while still keeping the platform accessible to non-developers.',
              ],
            },
          ],
        },
        {
          heading: 'Scene Creation & Layout',
          paragraphs: [
            'The Grid Editor supports the creation of multi-scene experiences, allowing users to define how environments are structured and how users progress through them.',
          ],
        },
        {
          paragraphs: ['Scenes can be arranged in:'],
          list: [
            'Linear flows for guided training or walkthroughs',
            'Branching paths for decision-based scenarios',
            'Scenario-based layouts for simulations and assessments',
          ],
          subSections: [
            {
              afterParagraphs: [
                'Transitions between scenes can be controlled through triggers, conditions, or user actions, enabling dynamic and context-aware experiences.',
              ],
            },
          ],
        },
        {
          heading: 'Asset Panel',
          paragraphs: [
            'The Asset Panel functions similarly to a layers panel, providing a structured view of all assets within a scene. Users can select specific assets to edit, group related elements, and manage visibility or hierarchy.',
            'This panel helps maintain clarity in complex scenes by allowing users to quickly locate, select, and modify individual assets without navigating the entire environment manually.',
          ],
        },
        {
          heading: 'Components',
          paragraphs: [
            'The Components tab provides access to a wide range of functional elements that enhance interactivity and realism. Users can add components such as:',
          ],
          list: [
            'Events and triggers',
            'Audio and video',
            'Materials and lighting',
            'Cameras and animations',
            'Physics and environmental behaviors',
            'Particles and visual effects',
          ],
          subSections: [
            {
              afterParagraphs: [
                'Components allow users to extend asset functionality and behavior without rebuilding scenes, enabling rapid iteration and modular design.',
              ],
            },
          ],
        },
        {
          heading: 'Transform Controls',
          paragraphs: [
            'Transform controls allow users to precisely manipulate assets within a scene. Assets can be moved, rotated, or scaled using intuitive controls.',
            'Users can switch between **orthographic** and **perspective** views to achieve accurate placement and alignment—especially useful when designing complex environments or spatial layouts.',
          ],
        },
        {
          heading: 'Add Assets',
          paragraphs: [
            'The Grid Editor supports a wide variety of asset types, including:',
          ],
          list: [
            '3D models',
            'Images and textures',
            'Videos and sprites',
            'Vector graphics',
            'Sound and audio effects',
          ],
          subSections: [
            {
              afterParagraphs: [
                'Users can import assets directly or access shared libraries to add pre-approved components, ensuring consistency and efficiency across projects.',
              ],
            },
          ],
        },
        {
          heading: 'Help',
          paragraphs: [
            'The Help section within the Grid Editor provides immediate access to:',
          ],
          list: [
            'Feedback and support channels',
            'This Help Center',
            'The Learning Center for onboarding guides and video tutorials',
            'Keyboard shortcuts and productivity tips',
          ],
          subSections: [
            {
              afterParagraphs: [
                'This ensures users can quickly find assistance without leaving the editor, supporting faster learning and smoother workflows.',
              ],
            },
          ],
        },
        {
          heading: 'Interaction & Behavior Configuration',
          paragraphs: [
            'Interactions within the Grid Editor are configured visually using triggers, conditions, feedback, and responses. This allows users to create complex logic—such as conditional flows, adaptive behaviors, and contextual responses—without deep coding.',
            'By separating logic from presentation, teams can refine interactions quickly and test variations without disrupting the overall experience structure.',
          ],
        },
        {
          heading: 'Publish & Review',
          paragraphs: [
            'Once an experience is ready, it can be published directly from the Grid Editor. Published experiences can then be reviewed by stakeholders, tested across devices, and refined based on feedback.',
            'This review-first approach ensures experiences meet business, operational, and compliance requirements before full deployment—supporting quality, consistency, and confidence at scale.',
          ],
        },
      ],
    },
    3: {
      intro:
        'The Prompt Bar allows users to interact with and manipulate assets, scenes, and behaviors using guided commands and AI-assisted inputs. Teams can quickly modify objects, adjust interactions, trigger behaviors, or refine scenarios. This capability accelerates iteration and reduces manual configuration effort—making it easier for business users and subject matter experts to participate actively in experience creation and refinement.',
      sections: [
        {
          heading: 'Prompt Bar Overview',
          paragraphs: [
            'The Prompt Bar is an intelligent interaction layer within Spatial Grid that allows users to communicate with the platform using guided commands. Instead of navigating multiple menus or configuration panels, users can describe what they want to change or achieve, and the platform translates those instructions into actions.',
            'This capability accelerates common tasks such as modifying assets, adjusting behaviors, updating scenes, or refining experience flow. The Prompt Bar is designed to work alongside the Grid Editor, enhancing productivity without replacing existing visual or technical workflows.',
          ],
        },
        {
          heading: 'AI-Assisted Actions',
          paragraphs: [
            'The Prompt Bar supports a wide range of AI-assisted actions that simplify and speed up experience creation and refinement. Users can perform tasks such as repositioning or scaling assets, changing materials, triggering animations, updating interaction logic, or modifying scene transitions through simple prompts.',
            'More complex changes—such as refining scenarios, adjusting conditional flows, or adapting behaviors based on context—can also be handled incrementally through guided prompts. This reduces manual configuration effort and allows teams to experiment, iterate, and validate ideas quickly.',
            'By automating repetitive or time-consuming steps, the Prompt Bar helps maintain consistency while freeing teams to focus on design quality and outcomes.',
          ],
        },
        {
          heading: 'Business User Enablement',
          paragraphs: [
            'The Prompt Bar significantly lowers the barrier to participation for non-technical users, including business stakeholders and subject matter experts. These users can suggest or implement changes directly, without needing to understand underlying code or platform complexity.',
            'This enables faster feedback cycles, closer collaboration between business and technical teams, and more accurate translation of domain knowledge into immersive experiences. As a result, organizations benefit from reduced iteration time, improved alignment, and experiences that more closely reflect real-world requirements.',
          ],
        },
      ],
    },
    4: {
      intro:
        'The Spatial Grid Library stores reusable assets such as 3D models, animations, environments, interaction templates, and logic components. Assets can be shared across projects to ensure consistency and reduce duplication. By centralizing assets, enterprises maintain quality control, accelerate development, and lower long-term maintenance effort. Updates to shared components can propagate across experiences when required.',
      sections: [
        {
          heading: 'Asset Library Overview',
          paragraphs: [
            'The Spatial Grid Asset Library is a centralized repository for reusable components used across immersive experiences. It stores a wide range of assets including 3D models, animations, environments, UI elements, interaction templates, and logic blocks.',
            'By centralizing these components, the Asset Library ensures teams work from a shared, approved set of resources. This reduces duplication, improves consistency, and enables faster creation of high-quality experiences across projects and teams.',
          ],
        },
        {
          heading: 'Reuse & Consistency',
          paragraphs: [
            'Assets stored in the library can be reused across multiple projects and workspaces. This ensures visual consistency, predictable behavior, and standardized interactions across all immersive deployments.',
            'Reusable components allow enterprises to establish design and interaction standards—helping teams build experiences that look, feel, and behave consistently, regardless of who creates them or where they are deployed.',
          ],
        },
        {
          heading: 'Versioning & Updates',
          paragraphs: [
            'The Asset Library supports centralized versioning and updates. When an asset is updated, changes can be propagated across all experiences that reference it, where appropriate.',
            'This approach reduces manual updates, minimizes errors, and ensures improvements or fixes are applied consistently. Enterprises can maintain control over when and how updates are rolled out, balancing stability with continuous improvement.',
          ],
        },
        {
          heading: 'Enterprise Benefits',
          paragraphs: [
            'Centralized asset management delivers significant enterprise value. Development cycles are accelerated by eliminating redundant work, quality control is improved through standardization, and maintenance effort is reduced through centralized updates.',
            'By lowering duplication and rework, the Asset Library contributes directly to lower total cost of ownership while enabling teams to scale immersive initiatives with confidence and efficiency.',
          ],
        },
      ],
    },
    5: {
      intro:
        'Spatial Grid supports collaboration across cross-functional teams, including administrators, creators, reviewers, instructors, and operators. Teams can be assigned to projects with clearly defined roles and permissions. Collaboration features enable structured reviews, feedback cycles, and controlled publishing—ensuring experiences meet business, operational, and compliance requirements before deployment.',
      sections: [
        {
          heading: 'Team Structure',
          paragraphs: [
            'Spatial Grid is designed to support cross-functional teams involved in enterprise immersive initiatives. Typical roles include administrators, creators, reviewers, instructors, operators, and viewers—each contributing at different stages of the experience lifecycle.',
            'This structure reflects real-world enterprise collaboration, where business owners, domain experts, technical teams, and operational users work together. By clearly defining team responsibilities, Spatial Grid helps organizations streamline collaboration while reducing overlap and confusion.',
          ],
        },
        {
          heading: 'Role-Based Access',
          paragraphs: [
            'Spatial Grid uses role-based access control to determine what actions users can perform within the platform. Roles define permissions such as viewing content, editing experiences, publishing deployments, or accessing analytics.',
            'By assigning access based on responsibility, enterprises ensure users interact only with relevant features and data. This minimizes risk, protects sensitive content, and supports compliance with internal security and governance policies.',
          ],
        },
        {
          heading: 'Review & Approval Workflows',
          paragraphs: [
            'Spatial Grid includes structured review and approval workflows to support quality control and compliance. Experiences can move through defined stages such as draft, review, approval, and production before deployment.',
            'Reviewers and approvers can provide feedback, request changes, and validate content accuracy before publishing. This is especially critical in regulated, safety-sensitive, or mission-critical environments where accuracy and accountability are essential.',
          ],
        },
        {
          heading: 'Multi-Team Coordination',
          paragraphs: [
            'Spatial Grid enables teams across departments, business units, or geographic locations to collaborate within a shared platform without conflict. Clear project boundaries, role definitions, and activity tracking help maintain accountability as initiatives scale.',
            'This coordinated approach allows enterprises to manage multiple immersive programs in parallel while ensuring clarity, ownership, and alignment across teams—supporting efficient collaboration at enterprise scale.',
          ],
        },
      ],
    },
    6: {
      intro:
        'Spatial Grid provides guided tutorials, walkthroughs, and contextual help designed for enterprise users. These resources help teams understand platform workflows, editor capabilities, and best practices. Tutorials are structured to support onboarding, upskilling, and role-based learning—allowing users to become productive quickly without extensive external training.',
      sections: [
        {
          heading: 'Guided Tutorials',
          paragraphs: [
            'Spatial Grid includes guided, step-by-step tutorials designed to help users quickly understand platform workflows, editor functionality, and core capabilities. These tutorials walk users through real tasks such as creating projects, building experiences, configuring interactions, and reviewing analytics.',
            'Tutorials are practical and task-driven, enabling users to learn by doing rather than through abstract explanations. This approach ensures knowledge is immediately applicable to real enterprise use cases.',
          ],
        },
        {
          heading: 'Contextual Help',
          paragraphs: [
            'Contextual help is embedded directly within the Spatial Grid interface, providing timely guidance exactly where users need it. Tooltips, inline tips, and prompts appear in response to user actions, reducing the need to search through external documentation.',
            'By delivering help in context, Spatial Grid minimizes disruption, supports continuous learning, and allows users to remain focused on their work while understanding new features or workflows.',
          ],
        },
        {
          heading: 'Role-Based Learning',
          paragraphs: [
            'Tutorials and learning paths in Spatial Grid are structured by role to reflect how different users interact with the platform. Business users focus on project oversight and analytics, creators on building experiences, instructors on delivery and assessment, and administrators on governance and access control.',
            'This role-based approach ensures users receive relevant guidance without unnecessary complexity—making learning more efficient and aligned with individual responsibilities.',
          ],
        },
        {
          heading: 'Faster Adoption',
          paragraphs: [
            'By combining guided tutorials, contextual help, and role-based learning paths, Spatial Grid significantly reduces onboarding time. Teams can become productive quickly without extensive external training or documentation.',
            'This accelerates adoption across departments, shortens time to value, and enables organizations to scale immersive initiatives with confidence and minimal friction.',
          ],
        },
      ],
    },
    7: {
      intro:
        'The Analytics section provides visibility into how experiences are used across teams and locations. Users can review engagement metrics, completion data, interaction patterns, and performance indicators. These insights help enterprises measure effectiveness, optimize experience design, and align immersive initiatives with training, operational, or engagement goals.',
      sections: [
        {
          heading: 'Analytics Overview',
          paragraphs: [
            'The Analytics section in Spatial Grid provides comprehensive visibility into how immersive experiences are used across teams, locations, and devices. It connects spatial interactions with measurable data, enabling enterprises to understand not just if experiences are used, but how they are experienced.',
            'By centralizing analytics across deployments, organizations gain a unified view of performance, engagement, and adoption—supporting informed decision-making at both operational and strategic levels.',
          ],
        },
        {
          heading: 'Key Metrics',
          subSections: [
            {
              title: 'Engagement & Time Spent',
              paragraphs: [
                'Spatial Grid tracks how long users spend within experiences, individual scenes, and specific interactions. This helps identify high-engagement areas as well as sections where attention drops.',
              ],
            },
            {
              title: 'Immersive Heatmaps & Session Recordings',
              paragraphs: [
                'Heatmaps visualize where users focus, move, and interact within immersive environments. Session recordings provide replayable views of user journeys, enabling teams to observe real behavior and identify friction points.',
              ],
            },
            {
              title: 'Completion & Drop-Off Rates',
              paragraphs: [
                'Analytics capture completion status for experiences, scenarios, and tasks. Drop-off points highlight where users disengage, helping teams refine flow, pacing, or clarity.',
              ],
            },
            {
              title: 'Interaction Patterns & Paths',
              paragraphs: [
                'Spatial Grid records the paths users take through experiences, including decision points and branching behavior. This reveals how users navigate scenarios and where logic or guidance may need adjustment.',
              ],
            },
            {
              title: 'Performance Indicators',
              paragraphs: [
                'For training and simulation use cases, performance indicators such as task accuracy, response timing, and procedural adherence can be tracked to assess skill development and readiness.',
              ],
            },
            {
              title: 'Business & Operational Insights',
              paragraphs: [
                'Analytics translate immersive activity into actionable insights for enterprise stakeholders. Business leaders can evaluate return on investment, training managers can measure learning effectiveness, and operations teams can identify process gaps or risk areas.',
                'By aligning analytics with organizational objectives, enterprises can validate outcomes, justify investments, and make confident, data-backed decisions about scaling or optimizing immersive initiatives.',
              ],
            },
            {
              title: 'Continuous Improvement',
              paragraphs: [
                'Insights generated through Spatial Grid analytics support continuous refinement of immersive experiences. Teams can iteratively adjust content, interaction logic, workflows, and scenarios based on real usage data rather than assumptions.',
                'This data-driven approach ensures experiences remain effective, relevant, and aligned with evolving business needs—maximizing long-term value and impact across deployments.',
              ],
            },
          ],
        },
      ],
    },
    8: {
      intro:
        'Spatial Grid notifications keep users informed about important events such as project updates, reviews, deployments, analytics alerts, and system changes. Activity monitoring ensures stakeholders stay aligned and responsive, supporting smoother collaboration and timely decision-making across enterprise teams.',
      sections: [
        {
          heading: 'Notification Types',
          paragraphs: [
            'Spatial Grid delivers notifications to keep users informed about important platform and project events. These include updates to projects, review and approval requests, deployments to devices or environments, analytics alerts, and system-level changes.',
            'Notifications are role-aware and context-specific, ensuring users receive only relevant information based on their responsibilities. This reduces noise while maintaining visibility into critical actions and decisions.',
          ],
        },
        {
          heading: 'Activity Feeds',
          paragraphs: [
            'The Activity Feed provides a detailed, chronological log of actions taken across the platform. Users can view who made changes, what was updated, and when actions occurred—across projects, assets, deployments, and configurations.',
            'This transparency supports accountability and traceability, particularly in enterprise environments where auditability and change history are important.',
          ],
        },
        {
          heading: 'Enterprise Alignment',
          paragraphs: [
            'Notifications and activity monitoring help keep stakeholders aligned across business, technical, and operational teams. By surfacing updates and requests in real time, Spatial Grid reduces dependency on external communication tools and minimizes delays.',
            'This shared visibility enables smoother collaboration, faster reviews, and more coordinated decision-making across departments and locations.',
          ],
        },
        {
          heading: 'Operational Awareness',
          paragraphs: [
            'Real-time notifications provide teams with continuous operational awareness. Users can quickly respond to issues, address review feedback, or act on analytics insights as they arise.',
            'By staying informed, teams reduce bottlenecks, maintain momentum, and ensure immersive initiatives progress efficiently—even across complex, multi-team deployments.',
          ],
        },
      ],
    },
  };

  const currentContent = contentData[activeLeft] || contentData[0];

  const renderText = (text) =>
    text.split(/(\*\*.*?\*\*)/g).map((part, index) =>
      part.startsWith('**') && part.endsWith('**') ? (
        <span key={index} className="font-medium text-white">
          {part.replace(/\*\*/g, '')}
        </span>
      ) : (
        <span key={index}>{part}</span>
      )
    );

  return (
    <div className="text-[#E4E4E4]">
      <p className="font-light text-[15px] leading-relaxed mb-8">
        {currentContent.intro}
      </p>

      {currentContent.sections.map((section, index) => {
        // Generate ID for scroll spy only if heading exists
        const sectionId = section.heading
          ? generateSectionId(section.heading)
          : '';

        return (
          <div key={index} className="mb-12">
            {/* Main Heading with ID for scroll spy */}
            {section.heading && (
              <h2
                id={sectionId}
                className="text-[22px] font-medium text-white mb-4 scroll-mt-28"
              >
                {section.heading}
              </h2>
            )}

            {/* 🔹 NORMAL PARAGRAPH SECTIONS */}
            {section.paragraphs &&
              section.paragraphs.map((paragraph, pIndex) => (
                <p
                  key={pIndex}
                  className="font-light text-[15px] leading-relaxed mb-4 text-[#E4E4E4]"
                >
                  {renderText(paragraph)}
                </p>
              ))}
            {section.list && (
              <ul className="list-disc pl-6 space-y-3 text-[15px] font-light text-[#E4E4E4] mb-4">
                {section.list.map((item, i) => (
                  <li key={i}>{renderText(item)}</li>
                ))}
              </ul>
            )}

            {/* 🔹 ADVANCED SECTIONS */}
            {section.subSections?.map((sub, subIndex) => (
              <div key={subIndex} className="mb-8">
                {/* Sub Heading */}
                {sub.title && (
                  <h3 className="text-[18px] font-medium text-white mb-3">
                    {sub.title}
                  </h3>
                )}

                {/* Sub Paragraphs (before list) */}
                {sub.paragraphs?.map((para, pIndex) => (
                  <p
                    key={pIndex}
                    className="font-light text-[15px] leading-relaxed mb-4 text-[#E4E4E4]"
                  >
                    {renderText(para)}
                  </p>
                ))}

                {/* Sub List */}
                {sub.list && (
                  <ul className="list-disc pl-6 space-y-3 text-[15px] font-light text-[#E4E4E4] mb-4">
                    {sub.list.map((item, i) => (
                      <li key={i}>{renderText(item)}</li>
                    ))}
                  </ul>
                )}

                {/* ✅ Paragraphs AFTER list */}
                {sub.afterParagraphs?.map((para, pIndex) => (
                  <p
                    key={pIndex}
                    className="font-light text-[15px] leading-relaxed mb-4 text-[#E4E4E4]"
                  >
                    {renderText(para)}
                  </p>
                ))}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};
